// Copyright (c) 2023 Jürgen Gebath
//
// This software is released under the MIT License.
// https://opensource.org/licenses/MIT
import { Grid, ImageList, ImageListItem, Typography, Box } from "@mui/material";
import { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
//import { activities } from "../data/activityData";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import parse from "html-react-parser";

export default function Activity(props) {
  let { activityId } = useParams();
  const [imageListIndex, setImageListIndex] = useState(-1);
  const [activityTitle, setActivityTitle] = useState("");
  const [activityContent, setActivityContent] = useState("");
  const [activityImageData, setActivityImageData] = useState([]);
  const [activityDate, setActivityDate] = useState("");
  const location = useLocation();
  const [showGallery, setShowGallery] = useState(
    location.state !== null && location.state.startImageGallery ? true : false
  );

  useEffect(() => {
    fetch(process.env.PUBLIC_URL + "/data/activityData.json", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      cache: "no-cache",
    })
      .then(function (originalResponse) {
        return originalResponse.json();
      })
      .then(function (jsonResponse) {
        let jsonRespWithDate = jsonResponse.map((element) => {
          let dateArray = element.date.match(
            /^(\d{4})\.(\d{2})\.(\d{2})\s*(\d{2})*:*(\d{2})*$/
          );
          element.date = new Date(
            dateArray[1],
            dateArray[2] - 1,
            dateArray[3],
            dateArray[4],
            dateArray[5]
          );
          return element;
        });
        let activityIndex = jsonRespWithDate.findIndex(
          (item) => format(item.date, "yyyyMMddHH") === activityId
        );
        let activityData = jsonRespWithDate[activityIndex];
        setActivityTitle(activityData.title);
        setActivityDate(
          format(activityData.date, "dd. MMMM yyyy", { locale: de })
        );
        setActivityContent(activityData.content);
        setImageListIndex(activityData.cardImageNr - 1);
        setActivityImageData(createItemMap(activityData));
      })
      .catch((err) => console.log(err));
  }, [activityId]);

  function createItemMap(activity) {
    const itemArray = [];
    let i = activity.firstImageNr;
    let j = activity.lastImageNr;
    let imagePath = process.env.PUBLIC_URL + activity.imageRegex;
    let item;
    if (Object.hasOwn(activity, "video")) {
      let videoPath = process.env.PUBLIC_URL + activity.video;
      item = { type: "video", original: videoPath, title: "" };
      itemArray.push(item);
    }
    while (i <= j) {
      let imageSrc = imagePath.replace("*", i);
      item = { type: "image", original: imageSrc, title: "" };
      itemArray.push(item);
      i++;
    }
    return itemArray;
  }

  const handleButtonClick = () => {
    setShowGallery(showGallery ? false : true);
  };

  return (
    <Grid
      container
      spacing={2}
      sx={(theme) => ({
        [theme.breakpoints.down("sm")]: {
          marginTop: 7,
        },
        [theme.breakpoints.up("sm")]: {
          marginTop: 20,
        },
      })}
      justifyContent="center">
      {!showGallery && (
        <Grid item>
          <Typography
            variant="h5"
            component="h2">
            {activityDate} - {activityTitle}
          </Typography>
          {typeof activityContent !== "undefined" && (
            <Box
              sx={(theme) => ({
                fontFamily: theme.typography.body2.fontFamily,
                fontWeight: theme.typography.body2.fontWeight,
                fontSize: theme.typography.body2.fontSize,
                lineHeight: theme.typography.body2.lineHeight,
                letterSpacing: theme.typography.body2.letterSpacing,
              })}>
              {parse(activityContent)}
            </Box>
          )}
          <ImageList
            variant="masonry"
            cols={1}
            gap={8}
            sx={{ marginTop: 2 }}>
            {activityImageData.map((item, index) => (
              <ImageListItem
                key={item.original}
                onClick={() => {
                  setImageListIndex(index);
                  handleButtonClick();
                }}>
                {item.type === "video" && (
                  <video
                    width="100%"
                    controls>
                    <source
                      src={item.original}
                      type="video/mp4"
                    />
                  </video>
                )}
              </ImageListItem>
            ))}
          </ImageList>
          <ImageList
            variant="masonry"
            cols={3}
            gap={8}
            sx={{ marginTop: 2 }}>
            {activityImageData.map((item, index) => (
              <ImageListItem
                key={item.original}
                onClick={() => {
                  setImageListIndex(index);
                  handleButtonClick();
                }}>
                {item.type === "image" && (
                  <img
                    src={item.original}
                    srcSet={item.original}
                    alt={item.title}
                    loading="lazy"
                  />
                )}
              </ImageListItem>
            ))}
          </ImageList>
        </Grid>
      )}
      {showGallery && (
        <Grid item>
          <ImageGallery
            items={activityImageData}
            lazyLoad={true}
            autoPlay={false}
            infinite={true}
            startIndex={imageListIndex}
            onClick={handleButtonClick}
          />
        </Grid>
      )}
    </Grid>
  );
}
